import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import { PlanNote } from "../components/PlanLockedHint";
import ProductBranchesModal from "../components/ProductBranchesModal";
import Toast from "../components/Toast";
import { param, useListUrlState, useScrollRestoration, useValidOption, yesNo } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBrands, fetchProductCategories } from "../services/catalogService";
import { hasFeature } from "../services/features";
import { rememberListSearch } from "../services/listMemory";
import { deleteProduct, fetchProducts } from "../services/productService";
import { labelFor } from "../services/enumLabels";

const CATALOGUE_FILTERS = {
  search: param.string("", { debounce: 400 }),
  category: param.string(),
  brand: param.string(),
  isActive: yesNo(),
  // Products needing stock at a branch this user can see.
  lowStock: param.flag(),
};

/**
 * One branch's stock as a chip: normal, below its threshold (amber), or empty
 * (red). The tooltip carries the threshold in force there — the branch's own,
 * else the product's. The API only sends branches this user can reach.
 */
function StockChip({ entry, productId, t, lang, tracked = true }) {
  // Untracked stock (no inventory in the plan): just where it is sold.
  if (!tracked) {
    return (
      <span className="inline-flex max-w-[14rem] items-center rounded-full border border-line bg-raised px-2.5 py-0.5 text-xs font-medium text-ink">
        <span className="truncate">{entry.name}</span>
      </span>
    );
  }

  const tone = entry.is_out_of_stock
    ? "border-danger-line bg-danger-wash text-danger-text"
    : entry.is_low_stock
      ? "border-warning-line bg-warning-wash text-warning-text"
      : "border-line bg-raised text-ink";
  const count = Number(entry.stock_quantity ?? 0).toLocaleString(lang);
  const state = entry.is_out_of_stock
    ? t("products.outOfStock")
    : entry.is_low_stock
      ? t("products.lowStock")
      : null;
  const tooltip = [
    entry.stock_threshold != null
      ? t("products.stockChip.threshold", { count: entry.stock_threshold })
      : t("products.stockChip.noThreshold"),
    state,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Link
      to={`/branches/${entry.branch_id}/products?adjust=${productId}`}
      title={tooltip}
      aria-label={`${entry.name} · ${count}${state ? ` · ${state}` : ""}. ${tooltip}`}
      className={`inline-flex max-w-[14rem] items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium hover:underline ${tone}`}
    >
      <span className="truncate">{entry.name}</span>
      <span aria-hidden="true">·</span>
      <span className="flex-shrink-0 tabular-nums" dir="ltr">
        {count}
      </span>
    </Link>
  );
}

/**
 * All Products: the business's product catalogue, the twin of the service
 * catalogue. A product is defined once here and sold nowhere until its row's
 * Branches action chooses where — with a price and an opening stock each.
 *
 * This used to be a branch picker that forwarded a single-branch business
 * straight into that branch's products screen, where creating a product put it
 * on the branch without anybody choosing that. The branch screen is still
 * there for stock and availability, reached from the branch itself.
 */
export default function Products() {
  const { t, i18n } = useTranslation();
  // The plan's inventory: without it there is no stock to show or filter.
  const tracksStock = hasFeature(getCurrentUser(), "inventory");
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Page, filters and search live in the URL — see useUrlState.
  const { page, setPage, filters, applyFilters, searchTerm, setSearchTerm, submitSearch } =
    useListUrlState(CATALOGUE_FILTERS);
  useScrollRestoration(!loading);
  // So Back from a branch's products screen lands on this list as it was.
  useEffect(() => rememberListSearch("/products", location.search), [location.search]);
  useValidOption(filters.category, categories.length ? categories : null, () =>
    applyFilters({ category: "" })
  );
  useValidOption(filters.brand, brands.length ? brands : null, () => applyFilters({ brand: "" }));

  const [branchesFor, setBranchesFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { products: list, meta: pagination } = await fetchProducts({ page, filters });
      setProducts(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("products.loadFailed")));
      setProducts([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, filters, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  useEffect(() => {
    Promise.all([fetchProductCategories(), fetchBrands()])
      .then(([categoryList, brandList]) => {
        setCategories(categoryList);
        setBrands(brandList);
      })
      .catch(() => {});
  }, [i18n.language]);

  const money = (value) =>
    Number(value ?? 0).toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });


  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteProduct(deleting.id);
      setToast({ type: "success", message: t("products.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("products.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("products.catalogueTitle")}</h1>
          <p className="text-sm text-ink-2">{t("products.catalogueSubtitle")}</p>
        </div>
        <Link to="/products/create" className="btn-primary">
          {t("products.new")}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card mb-4 grid gap-3 p-4 md:grid-cols-5">
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="product-search">
            {t("products.filters.search")}
          </label>
          <input
            id="product-search"
            className="field-input"
            placeholder={t("products.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="product-category">
            {t("products.filters.category")}
          </label>
          <select
            id="product-category"
            className="field-input"
            value={filters.category}
            onChange={(e) => applyFilters({ category: e.target.value })}
          >
            <option value="">{t("products.filters.any")}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.parent ? `${category.parent.name} › ${category.name}` : category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="product-brand">
            {t("products.filters.brand")}
          </label>
          <select
            id="product-brand"
            className="field-input"
            value={filters.brand}
            onChange={(e) => applyFilters({ brand: e.target.value })}
          >
            <option value="">{t("products.filters.any")}</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col justify-end gap-2">
          {tracksStock && (
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line-strong"
                checked={filters.lowStock}
                onChange={(e) => applyFilters({ lowStock: e.target.checked })}
              />
              {t("products.filters.lowStockOnly")}
            </label>
          )}
          <button type="button" className="btn-primary w-full" onClick={submitSearch}>
            {t("products.filters.apply")}
          </button>
        </div>
      </div>

      {!tracksStock && <PlanNote className="mb-4">{t("plan.inventoryOff")}</PlanNote>}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : products.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {filters.lowStock ? t("products.noLowStock") : t("products.catalogueEmpty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("products.table.product")}</th>
                  <th className="px-4 py-3">{t("products.table.category")}</th>
                  <th className="px-4 py-3">{t("products.table.basePrice")}</th>
                  <th className="px-4 py-3">{t("products.table.status")}</th>
                  <th className="px-4 py-3">{t("products.table.branches")}</th>
                  <th className="px-4 py-3 text-end">{t("products.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {product.primary_image ? (
                          <img
                            src={product.primary_image.url}
                            alt=""
                            className="h-9 w-9 flex-shrink-0 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-raised text-xs text-ink-2">
                            —
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium">{product.name}</p>
                          {product.brand?.name && (
                            <p className="truncate text-xs text-ink-2">{product.brand.name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {product.category?.name ?? t("common.none")}
                    </td>
                    <td className="px-4 py-3 text-ink-2" dir="ltr">
                      {money(product.base_price ?? product.price)}
                      <span className="ms-1 text-xs text-ink-2">/ {labelFor("unit", product.unit)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {product.is_active ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("products.active")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text">
                          {t("products.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {(product.branches ?? []).length === 0 ? (
                        t("products.soldNowhere")
                      ) : (
                        <div className="flex min-w-[12rem] flex-wrap gap-1.5">
                          {product.branches.map((entry) => (
                            <StockChip
                              key={entry.branch_id}
                              entry={entry}
                              productId={product.id}
                              t={t}
                              lang={i18n.language}
                              tracked={tracksStock}
                            />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-end">
                      <button
                        type="button"
                        onClick={() => setBranchesFor(product)}
                        className="text-sm font-semibold text-accent-text hover:underline"
                      >
                        {t("products.branches.action")}
                      </button>
                      <Link
                        to={`/products/${product.id}/edit`}
                        className="ms-3 text-sm font-semibold text-accent-text hover:underline"
                      >
                        {t("common.edit")}
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError("");
                          setDeleting(product);
                        }}
                        className="ms-3 text-sm font-semibold text-danger-text hover:underline"
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("common.previous")}
            </button>
            <span className="text-ink-2">
              {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      <ProductBranchesModal
        product={branchesFor}
        open={Boolean(branchesFor)}
        onClose={() => setBranchesFor(null)}
        onSaved={() => {
          setBranchesFor(null);
          setToast({ type: "success", message: t("products.branches.saved") });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("products.deleteTitle")}
        message={t("products.deleteMessage", { name: deleting?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
